import React from "react";
import { Box, Typography, Grid } from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";
import LayersIcon from "@mui/icons-material/Layers";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import EngineeringIcon from "@mui/icons-material/Engineering";
import { motion } from "framer-motion";
import welcomeImage from "../Images/welcomeImage.jpg";

const HeroSection = () => {
  const features = [
    {
      title: "One-Stop Solution",
      icon: <BuildIcon sx={{ fontSize: 50, color: "#C62828" }} />,
      description:
        "3D Printing, 3D CAD designing, Acrylic Fabrication & Laser Cutting",
    },
    {
      title: "Industrial-Grade Quality",
      icon: (
        <PrecisionManufacturingIcon sx={{ fontSize: 50, color: "#C62828" }} />
      ),
      description: "Accuracy and reliability with high-end machines",
    },
    {
      title: "Wide Range of Materials & Finishes",
      icon: <LayersIcon sx={{ fontSize: 50, color: "#C62828" }} />,
      description: "Choose from a vast selection tailored to your needs",
    },
    {
      title: "Best Price Guarantee",
      icon: <MonetizationOnIcon sx={{ fontSize: 50, color: "#C62828" }} />,
      description: "Competitive pricing without compromising quality",
    },
    {
      title: "Fastest Lead Times",
      icon: <DeviceHubIcon sx={{ fontSize: 50, color: "#C62828" }} />,
      description: "Get parts delivered in as little as 24–72 hours",
    },
    {
      title: "End-to-End Product Development",
      icon: <EngineeringIcon sx={{ fontSize: 50, color: "#C62828" }} />,
      description: "From concept to final product, all under one roof",
    },
    {
      title: "No MOQ",
      icon: <FormatListNumberedIcon sx={{ fontSize: 50, color: "#C62828" }} />,
      description: "From 1 piece to 1000+ parts – we’ve got you covered",
    },
    {
      title: "Expertise That Delivers",
      icon: <LayersIcon sx={{ fontSize: 50, color: "#C62828" }} />,
      description:
        "Specialists in fixtures, jigs, tooling parts, housings, enclosures & prototypes",
    },
  ];

  // Framer Motion variants for staggered children
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          backgroundColor: "#121212",
          color: "white",
          padding: "4rem 2rem",
          textAlign: "center",
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <Typography
            variant="h2"
            sx={{ fontWeight: 300, fontSize: "4rem", mb: 2 }}
          >
            Welcome to <span style={{ color: "#C62828" }}>Third Axis</span>
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.5, delay: 0.3 }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 300,
              fontSize: "1.5rem",
              maxWidth: "800px",
              mx: "auto",
              mb: 4,
              lineHeight: 1.6,
            }}
          >
            High-quality 3D printing services for all your needs! Precision,
            speed, and innovation delivered every time. From prototypes to
            production, we've got you covered.
          </Typography>
        </motion.div>
      </Box>

      {/* Feature Highlights Section */}
      <Box
        sx={{
          backgroundImage: `url(${welcomeImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
          py: 10,
          px: 4,
          color: "white",
          textAlign: "center",
          transform: "translateZ(0)",
          backfaceVisibility: "hidden",
          willChange: "transform",
        }}
      >
        {/* Overlay */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            zIndex: 1,
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            maxWidth: "1200px",
            mx: "auto",
          }}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            <Typography
              variant="h4"
              sx={{
                color: "white",
                fontWeight: "bold",
                mb: 6,
                fontSize: "2rem",
                borderBottom: "4px solid #C62828",
                display: "inline-block",
                pb: 1,
              }}
            >
              Why Choose Third Axis
            </Typography>
          </motion.div>

          {/* Grid with staggered motion */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Grid container spacing={6} justifyContent="center">
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div
                    variants={itemVariants}
                    whileHover={{
                      scale: 1.08,
                      rotate: -1,
                      transition: { type: "spring", stiffness: 200 },
                    }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Box sx={{ px: 2 }}>
                      <motion.div
                        whileHover={{
                          rotate: [0, -10, 10, 0],
                          transition: { duration: 0.5 },
                        }}
                      >
                        <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                      </motion.div>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#ccc",
                          fontSize: "0.95rem",
                          lineHeight: 1.6,
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Box>
      </Box>
    </>
  );
};

export default HeroSection;
